import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import React, { useState, useRef, useEffect } from "react";

interface HeaderProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  logout: () => void;
}

export default function Header({ theme, setTheme, logout }: HeaderProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const profileRef = useRef<HTMLDivElement>(null);

  // Fixed active link detection
  const isActive = (href: string) => {
    // Special case for dashboard - only active on exact match
    if (href === "/admin-dashboard") {
      return pathname === href;
    }
    // For all other links, check if path starts with href
    return pathname.startsWith(href);
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen]);

  return (
    <header
      className={`${
        theme === "dark" ? "bg-gray-50" : "bg-gray-50"
      } text-white shadow-md sticky top-0 z-50`}
    >
      {/* Top Navigation */}
      <nav
        className={`${
          theme === "dark" ? "bg-gray-50" : "bg-gray-50"
        } sticky top-0 z-50 shadow-md`}
      >
        <div className="max-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/admin-dashboard" className="flex-shrink-0 flex items-center">
                <div className="inline-flex items-center justify-center  backdrop-blur-sm m-3">
                  <img src={logo} alt="Valuatify Logo" className="h-12 rounded-lg" />
                </div>
                <span className="ml-3 text-gray-600 text-2xl font-bold">
                  Admin Panel
                </span>
              </Link>
            </div>

            <div className="ml-4 flex items-center md:ml-6">
              <button
                className="w-10 h-10 rounded-full cursor-pointer bg-gray-100/10 hover:bg-gray-400/5 flex items-center justify-center transition-all duration-300"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? "☀️" : "🌙"}
              </button>

              <div className="ml-3 relative" ref={profileRef}>
                <div>
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center cursor-pointer text-sm rounded-md bg-gray-800/10 text-gray-800 px-3 py-2 hover:bg-gray-800/20"
                  >
                    <span className="mr-2">👤</span>
                    Admin
                    <span className="ml-1">▼</span>
                  </button>
                </div>

                {profileMenuOpen && (
                  <div
                    className={`origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                      theme === "dark" ? "bg-gray-500" : "bg-white"
                    } ring-1 ring-black ring-opacity-5 py-1 z-50`}
                  >
                    <a
                      href="#"
                      className={`block px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "hover:bg-gray-500 text-white"
                          : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      Profile Settings
                    </a>
                    <Link
                      to="/staffDashboard"
                      className={`block px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "hover:bg-gray-500 text-white"
                          : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      Dashboard
                    </Link>

                    <Link
                      to="/auditorDashboard"
                      className={`block px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "hover:bg-gray-500 text-white"
                          : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      Data Collector
                    </Link>
                    <a
                      href="#"
                      className={`block px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "hover:bg-gray-500 text-white"
                          : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      Account Security
                    </a>
                    <a
                      href="#"
                      className={`block px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "hover:bg-gray-600 text-white"
                          : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      Preferences
                    </a>
                    <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                    <span
                      onClick={() => {
                        logout();
                        window.location.href = "/login";
                      }}
                      className={`block px-4 py-2 text-sm text-red-600 ${
                        theme === "dark"
                          ? "hover:bg-gray-600"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      Logout
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}